import { Injectable } from '@angular/core';
import { Observable, Subject, concat, defer, distinctUntilChanged, from, map, merge, of, shareReplay, switchMap, take } from 'rxjs';
import { UserDataShared } from '../models/interface/userData.interface';
import { ChatCityResolver } from './chat-city.resolver';
import { ChatExperimentClient, ChatExperimentQuery } from './chat-experiment.client';
import { ChatCountryCode } from './chat-experiment.config';
import { ChatState, toChatVariant } from './chat-variant';
import { MuChatService } from './mu-chat.service';
import { UserDataService } from './user-data.service';

const NONE: ChatState = { kind: 'none' };
const LAUNCHER: ChatState = { kind: 'launcher' };
const SELECTOR: ChatState = { kind: 'selector' };
const RESOLVING: ChatState = { kind: 'resolving' };

const toUserId = (id: string | undefined): number | null => {
  const value = Number(id);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const sameState = (a: ChatState, b: ChatState): boolean =>
  a.kind === b.kind && (a.kind !== 'arena' || a.url === (b as typeof a).url) && (a.kind !== 'freshchat' || a.opened === (b as typeof a).opened);

/**
 * Decides which chat the host renders. A logged-in user is assigned by the experiment
 * as soon as the session exists; an anonymous visitor only after pressing the launcher.
 * The experiment is asked once per user, so a token refresh only refreshes the ARENA url.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatVariantService {
  private readonly launcherPressed$ = new Subject<void>();
  private readonly countrySelected$ = new Subject<ChatCountryCode>();
  private readonly selectorDismissed$ = new Subject<void>();

  readonly state$: Observable<ChatState> = this.userDataService.getUserData().pipe(
    map(userData => (userData?.id ? userData : null)),
    distinctUntilChanged((a, b) => a?.id === b?.id),
    switchMap(userData => (userData ? this.userState(userData) : this.anonymousState())),
    distinctUntilChanged(sameState),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private userDataService: UserDataService,
    private muChatService: MuChatService,
    private experimentClient: ChatExperimentClient,
    private cityResolver: ChatCityResolver
  ) {}

  launcherPressed(): void {
    this.launcherPressed$.next();
  }

  countrySelected(country: ChatCountryCode): void {
    this.countrySelected$.next(country);
  }

  selectorDismissed(): void {
    this.selectorDismissed$.next();
  }

  private userState(userData: UserDataShared): Observable<ChatState> {
    return this.muChatService.restricted$.pipe(
      distinctUntilChanged(),
      switchMap(restricted => {
        if (restricted) {
          return of(NONE);
        }
        const userId = toUserId(userData.id);
        const cityId = this.cityResolver.forUser(userData);
        return userId && cityId ? this.experimentState({ userId, cityId }, false) : this.variantState(of(null), false);
      })
    );
  }

  private anonymousState(): Observable<ChatState> {
    return concat(
      of(LAUNCHER),
      this.launcherPressed$.pipe(
        take(1),
        switchMap(() =>
          concat(
            of(RESOLVING),
            from(this.cityResolver.fromGeolocation()).pipe(
              switchMap(cityId => (cityId ? this.experimentState({ cityId }, true) : this.selectorState()))
            )
          )
        )
      )
    );
  }

  private selectorState(): Observable<ChatState> {
    return concat(
      of(SELECTOR),
      merge(this.countrySelected$.pipe(map(country => this.cityResolver.forCountry(country))), this.selectorDismissed$.pipe(map(() => null))).pipe(
        take(1),
        switchMap(cityId => (cityId ? this.experimentState({ cityId }, true) : defer(() => this.anonymousState())))
      )
    );
  }

  private experimentState(query: ChatExperimentQuery, opened: boolean): Observable<ChatState> {
    return concat(of(RESOLVING), this.variantState(this.experimentClient.validate(query), opened));
  }

  /** Waits for one experiment answer, then follows the ARENA url without asking again. */
  private variantState(validation$: Observable<boolean | null>, opened: boolean): Observable<ChatState> {
    return validation$.pipe(
      take(1),
      switchMap(validation =>
        this.muChatService.iframeUrl$.pipe(
          map(url => (toChatVariant(validation, url) === 'arena' ? { kind: 'arena' as const, url } : { kind: 'freshchat' as const, opened }))
        )
      )
    );
  }
}
